'use client';

import { c as _c } from "react/compiler-runtime";
import { CMSLink } from '@/components/Link';
import { Media } from '@/components/Media';
import RichText from '@/components/RichText';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CirclePlay } from 'lucide-react';
import React, { useState, forwardRef } from 'react';
import YouTube from 'react-youtube';
import { motion } from 'motion/react';

// Define a custom button component to handle the ref properly
const TriggerButton = forwardRef((props, ref) => {
  const $ = _c(12);
  let t0;
  if ($[0] !== props.image) {
    t0 = <Media resource={props.image} className="w-full h-full object-cover" />;
    $[0] = props.image;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="absolute inset-0 z-10 bg-black/40 hover:bg-black/30 transition-colors duration-300" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0,
      scale: 0.8
    };
    t3 = {
      opacity: 1,
      scale: 1
    };
    t4 = {
      scale: 1.1
    };
    t5 = {
      type: "spring",
      stiffness: 300,
      damping: 20
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <motion.div initial={t2} animate={t3} whileHover={t4} transition={t5} className="absolute z-20"><CirclePlay className="size-12 md:size-16 text-white" /></motion.div>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== props.onClick || $[9] !== ref || $[10] !== t0) {
    t7 = <span ref={ref} className="relative flex w-full aspect-video items-center justify-center rounded-sm overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer" onClick={props.onClick}>{t0}{t1}{t6}</span>;
    $[8] = props.onClick;
    $[9] = ref;
    $[10] = t0;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
});
TriggerButton.displayName = 'TriggerButton';

// Helper function to extract YouTube video ID from URL
const getYouTubeVideoId = url => {
  if (!url) return null;

  // Match YouTube URL patterns
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};
export const Layout11Block = t0 => {
  const $ = _c(50);
  const {
    tagline,
    description,
    buttons,
    image,
    video,
    youtube_video,
    subHeadings,
    theme,
    heading
  } = t0;
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const t1 = youtube_video || "";
  let t2;
  if ($[0] !== t1) {
    t2 = getYouTubeVideoId(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const youtubeVideoId = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      hidden: t3,
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.15,
          delayChildren: 0.2
        }
      }
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const containerVariants = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      opacity: 0,
      y: 20
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [0.22, 1, 0.36, 1];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      hidden: t5,
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.6,
          ease: t6
        }
      }
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const itemVariants = t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      once: true,
      margin: "-100px"
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== tagline) {
    t9 = tagline && <motion.p className="tagline !text-slate-400 mb-3 font-semibold md:mb-4" variants={itemVariants}>{tagline}</motion.p>;
    $[8] = tagline;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== heading) {
    t10 = heading && <motion.h2 className="text-3xl mb-4 font-bold text-slate-900 md:text-4xl lg:text-5xl" variants={itemVariants}>{heading}</motion.h2>;
    $[10] = heading;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== description) {
    t11 = description && <motion.div variants={itemVariants}><RichText data={description} enableProse={true} className="prose-headings:mb-4 prose-p:text-lg md:prose-p:text-xl mb-6" /></motion.div>;
    $[12] = description;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== subHeadings) {
    t12 = subHeadings && subHeadings.length > 0 && <motion.div className="grid grid-cols-1 gap-6 py-2 sm:grid-cols-2 mb-6" variants={itemVariants}>{subHeadings?.map(_temp)}</motion.div>;
    $[14] = subHeadings;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== buttons) {
    t13 = buttons && buttons.length > 0 && <motion.div className="flex flex-wrap items-center gap-4" variants={itemVariants}>{buttons?.map(_temp2)}</motion.div>;
    $[16] = buttons;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t12 || $[21] !== t13 || $[22] !== t9) {
    t14 = <motion.div className="flex flex-col" initial="hidden" whileInView="visible" viewport={t8} variants={containerVariants}>{t9}{t10}{t11}{t12}{t13}</motion.div>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t9;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      opacity: 0,
      scale: 0.95
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.7,
        delay: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    };
    t17 = {
      once: true,
      margin: "-100px"
    };
    $[25] = t16;
    $[26] = t17;
  } else {
    t16 = $[25];
    t17 = $[26];
  }
  let t18;
  if ($[27] !== image) {
    t18 = <DialogTrigger className="!rounded-sm overflow-hidden w-full max-w-md md:max-w-none"><TriggerButton image={image} /></DialogTrigger>;
    $[27] = image;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <DialogTitle className="sr-only">Video</DialogTitle>;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== isIframeLoaded) {
    t20 = isIframeLoaded && <div className="w-full h-full" />;
    $[30] = isIframeLoaded;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== video || $[33] !== youtubeVideoId) {
    t21 = video && !youtubeVideoId && <Media resource={video} onLoad={() => setIsIframeLoaded(true)} />;
    $[32] = video;
    $[33] = youtubeVideoId;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== youtubeVideoId) {
    t22 = youtubeVideoId && <div className="aspect-video w-full h-full"><YouTube videoId={youtubeVideoId} className="w-full h-full" opts={{
        width: "100%",
        height: "100%",
        playerVars: {
          autoplay: 1
        }
      }} onReady={() => setIsIframeLoaded(true)} /></div>;
    $[35] = youtubeVideoId;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t20 || $[38] !== t21 || $[39] !== t22) {
    t23 = <DialogContent className="!rounded-sm max-w-3xl w-[90vw]">{t19}{t20}{t21}{t22}</DialogContent>;
    $[37] = t20;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t18 || $[42] !== t23) {
    t24 = <motion.div className="w-full flex justify-center" initial={t15} whileInView={t16} viewport={t17}><Dialog>{t18}{t23}</Dialog></motion.div>;
    $[41] = t18;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t14 || $[45] !== t24) {
    t25 = <div className="container mx-auto md:px-6"><div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-16 lg:gap-24 items-center">{t14}{t24}</div></div>;
    $[44] = t14;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t25 || $[48] !== theme) {
    t26 = <section data-theme={theme} className="bg-accent-4 px-4 py-8 md:py-24 lg:py-28">{t25}</section>;
    $[47] = t25;
    $[48] = theme;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  return t26;
};
function _temp(subHeading, index) {
  return <motion.div key={index} className="flex flex-col" variants={{
    hidden: {
      opacity: 0,
      y: 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.1,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  }}>{subHeading.icon && <div className="mb-3 md:mb-4">{subHeading.icon.url?.endsWith(".svg") ? <div className="svgIcon svgIcon-medium" style={{
        width: "4rem",
        height: "4rem",
        maskImage: `url("${encodeURI(subHeading.icon?.url)}")`,
        WebkitMaskImage: `url("${encodeURI(subHeading.icon?.url)}")`,
        maskRepeat: "no-repeat",
        maskSize: "contain",
        backgroundColor: "var(--accent)"
      }} /> : <Media resource={subHeading.icon} imgClassName="size-12" />}</div>}{subHeading.description && <RichText data={subHeading.description} enableProse={true} className="prose-headings:mb-4" />}</motion.div>;
}
function _temp2(button, index_0) {
  return <motion.div key={index_0} initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      delay: 0.5 + index_0 * 0.1,
      ease: [0.22, 1, 0.36, 1]
    }
  }} viewport={{
    once: true
  }}><CMSLink {...button.link} /></motion.div>;
}